import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, AlertTriangle } from "lucide-react";

/**
 * One-click contest demo entry.
 * Calls POST /api/auth/demo-login, stores the synthetic session, and lands the
 * judge directly on the student panel with a labeled synthetic profile.
 *
 * IMPORTANT: the endpoint issues a brand-new isolated demo user per call.
 * It NEVER weakens the regular verified_user guard — the demo user simply has
 * email_verified=true and is_demo=true so the same guard passes.
 */
export default function Demo() {
  const { loginDemo, user } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    // If somehow the user is already logged in, skip straight to the panel.
    if (user && user !== false) {
      nav("/panel", { replace: true });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        await loginDemo();
        setStatus("done");
        nav("/panel?demo=1", { replace: true });
      } catch (e) {
        setStatus("error");
        setError(formatApiError(e));
      }
    })();
  }, [loginDemo, nav, user]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-zinc-50"
      data-testid="demo-page"
    >
      <div className="text-center max-w-sm px-6">
        {status === "loading" && (
          <>
            <Loader2 className="w-8 h-8 mx-auto text-[#1E3A8A] animate-spin" />
            <h1 className="mt-5 text-lg font-semibold text-zinc-900">
              Demo öğrenci hazırlanıyor
            </h1>
            <p className="mt-2 text-sm text-zinc-600">
              Sana özel bir sentetik oturum açıyoruz. Bir saniye…
            </p>
          </>
        )}
        {status === "error" && (
          <div data-testid="demo-error">
            <AlertTriangle className="w-8 h-8 mx-auto text-red-500" />
            <h1 className="mt-5 text-lg font-semibold text-zinc-900">
              Demo oturumu başlatılamadı
            </h1>
            <p className="mt-2 text-sm text-red-600 break-words">{error}</p>
            <a
              href="/"
              className="mt-6 inline-block text-sm text-[#1E3A8A] hover:underline"
            >
              Ana sayfaya dön
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
